import type { ApiError } from "@rbim/shared";

const baseUrl = (import.meta.env.VITE_API_URL as string | undefined) ?? "/api";
let csrfToken: string | null = null;
let authenticationFailureHandler: (() => void) | null = null;

export function setCsrfToken(value: string | null) {
  csrfToken = value;
}

export function setAuthenticationFailureHandler(handler: (() => void) | null) {
  authenticationFailureHandler = handler;
}

export class ApiRequestError extends Error {
  constructor(readonly status: number, readonly code: string, message: string) {
    super(message);
  }
}

export async function apiRequest<T>(path: string, init: RequestInit = {}): Promise<T> {
  const headers = new Headers(init.headers);
  if (init.body && !headers.has("content-type")) headers.set("content-type", "application/json");
  if (csrfToken && !["GET", "HEAD"].includes((init.method ?? "GET").toUpperCase())) {
    headers.set("x-csrf-token", csrfToken);
  }

  let response: Response;
  try {
    response = await fetch(`${baseUrl}${path}`, { ...init, headers, credentials: "include" });
  } catch (error) {
    if (init.signal?.aborted) {
      throw new DOMException("The request was cancelled or timed out.", "AbortError");
    }
    throw new ApiRequestError(
      0,
      "API_UNAVAILABLE",
      "RBIM Server API is unavailable. Wait for the local RBIM services to start, then try again.",
    );
  }

  if (!response.ok) {
    const body = await response.json().catch(() => null) as ApiError | null;
    const code = body?.error.code ?? "INTERNAL_ERROR";
    if (
      response.status === 401
      || code === "ACCOUNT_SUSPENDED"
      || code === "ACCOUNT_DEACTIVATED"
    ) {
      csrfToken = null;
      authenticationFailureHandler?.();
    }
    throw new ApiRequestError(response.status, code, body?.error.message ?? "The request failed.");
  }
  if (response.status === 204) return undefined as T;
  return response.json() as Promise<T>;
}
